import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

const CollectedByCustomerForm = NiceModal.create(({ status, otoOrderID, orderID }) => {
	// Hooks and states declaration
	const modal = useModal();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const date = new Date();
	const { showError, showSuccess } = useAlert();
	const defaultColSpan = {
		md: 6
	};

	const OTPstatus = useSelector((state) => state.user?.clickAndCollectOTPStatus);

	const [isLoading, setIsLoading] = useState(false);

	// Form & Schema setup
	const schema = useMemo(
		() =>
			OTPstatus
				? yup.object().shape({
						status: yup.string(),
						deliveryDate: yup.date().required(),
						comments: yup.string().nullable(),
						otpCode: yup.number().nullable().required()
					})
				: yup.object().shape({
						status: yup.string(),
						deliveryDate: yup.date().required(),
						comments: yup.string().nullable(),
						otpCode: yup.number().nullable()
					}),
		[OTPstatus]
	);
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			status,
			deliveryDate: date.toISOString(Date.now()),
			comments: null,
			otpCode: null
		}
	});

	const { handleSubmit, setValue, watch } = formMethods;

	const dateWatcher = watch('deliveryDate');

	useEffect(() => {
		setValue('orderID', orderID);
	}, [orderID]);

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(ORDER_ACTIONS.UPDATE_COLLECTED_BY_CUSTOMER, {
				otoOrderID,
				deliveryDate: data.deliveryDate,
				comments: data.comments,
				otpCode: data.otpCode ?? undefined
			});

			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListsInPage());
				modal.remove();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.O009') {
				showError({
					message: 'errorMsg.otpNotMatching'
				});
			} else {
				AppLogger.error(error);
				showError();
				modal.remove();
			}
		}
		setIsLoading(false);
	};

	return (
		<Modal maxWidth="medium" fullwidth className="modal-collectedByCustomer">
			<ModalTitle>
				<Typography variant="p1">collectedByCustomerForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="status" colSpan={defaultColSpan}>
						<Status name="status" value={status} />
					</FormItem>

					<FormItem label="orderNumber" colSpan={defaultColSpan}>
						<StringInput name="orderID" disabled={true} />
					</FormItem>

					<FormItem label={'deliveryDate'} colSpan={defaultColSpan} required>
						<DateInput
							name="deliveryDate"
							value={dateWatcher}
							setValue={(value) => {
								setValue('deliveryDate', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					{OTPstatus && (
						<FormItem label="deliveryConfirmationCode" colSpan={defaultColSpan} required={true}>
							<IntegerInput name="otpCode" />
						</FormItem>
					)}

					<FormItem label="comments">
						<TextArea name="comments" placeholder="" />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						NiceModal.show(ConfirmationModal, {
							yesAction: () => {
								modal.remove();
							}
						});
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faFloppyDisk}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default CollectedByCustomerForm;
